import { useEffect, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { StopTimeType, StopType } from "types/MultiStop";
import type { ScheduleStopValue } from "@RequestFormV2/defaults/scheduleDefaults";
import { useSchedule } from "@RequestFormV2/sections/Schedule/context";
import {
  computeDeliveryAfterDateFromServiceLevel,
  isDeliveryDatetimeLockedByServiceLevel,
  isDeliveryServiceLevelFeatureActive,
  isNumericDeliveryServiceLevel,
  shouldShowDeliveryDatetimeFields,
  shouldShowPickupDateRequiredMessage,
} from "@RequestFormV2/sections/Schedule/utils/deliveryServiceLevel";
import { getStopFieldPath } from "@RequestFormV2/sections/Schedule/utils/fieldPaths";

const SERVICE_LEVEL_PATH = "service.service_level";
const DEFAULT_DELIVERY_TIME = "17:00:00";

export function useDeliveryServiceLevel(stopIndex: number) {
  const { setValue, getValues } = useFormContext();
  const { scheduleOptions } = useSchedule();
  const stops = useWatch({ name: "schedule.stops" }) as ScheduleStopValue[] | undefined;
  const serviceLevel = useWatch({ name: SERVICE_LEVEL_PATH }) as string | undefined;
  const pickupAfterDate = useWatch({ name: getStopFieldPath(0, "stop_after_date") }) as string | null;

  const stopCount = stops?.length ?? 0;
  const serviceLevelsEnabled = Boolean(scheduleOptions?.service_levels);
  const featureActiveForRequest = isDeliveryServiceLevelFeatureActive(serviceLevelsEnabled, stopCount);

  const stopType = stops?.[stopIndex]?.stop_type;
  const isDeliveryStop = stopType === StopType.DROPOFF;
  const showServiceLevelSelect = featureActiveForRequest && isDeliveryStop;
  const hasPickupDate = Boolean(pickupAfterDate);
  const showDeliveryDatetimeFields = shouldShowDeliveryDatetimeFields(
    showServiceLevelSelect,
    serviceLevel,
    hasPickupDate,
  );
  const showPickupDateRequiredMessage = shouldShowPickupDateRequiredMessage(
    showServiceLevelSelect,
    serviceLevel,
    hasPickupDate,
  );
  const isDeliveryDatetimeLocked = isDeliveryDatetimeLockedByServiceLevel(showServiceLevelSelect, serviceLevel);

  useEffect(() => {
    if (serviceLevelsEnabled && stopCount > 2 && getValues(SERVICE_LEVEL_PATH)) {
      setValue(SERVICE_LEVEL_PATH, "", { shouldDirty: true });
    }
  }, [serviceLevelsEnabled, stopCount, getValues, setValue]);

  useEffect(() => {
    if (!showServiceLevelSelect || !isNumericDeliveryServiceLevel(serviceLevel)) return;

    const nextDate = computeDeliveryAfterDateFromServiceLevel(pickupAfterDate, serviceLevel);
    if (!nextDate) return;

    setValue(getStopFieldPath(stopIndex, "stop_time_type"), StopTimeType.BEFORE, { shouldDirty: true });
    setValue(getStopFieldPath(stopIndex, "stop_before_date"), nextDate, { shouldDirty: true });
    setValue(getStopFieldPath(stopIndex, "stop_after_date"), null);
    setValue(getStopFieldPath(stopIndex, "stop_after_time"), null);

    const beforeTimePath = getStopFieldPath(stopIndex, "stop_before_time");
    if (!getValues(beforeTimePath)) {
      setValue(beforeTimePath, DEFAULT_DELIVERY_TIME, { shouldDirty: true });
    }
  }, [showServiceLevelSelect, serviceLevel, pickupAfterDate, stopIndex, getValues, setValue]);

  useEffect(() => {
    if (!showServiceLevelSelect || serviceLevel !== "NA") return;

    setValue(getStopFieldPath(stopIndex, "stop_time_type"), StopTimeType.BEFORE, { shouldDirty: true });
    setValue(getStopFieldPath(stopIndex, "stop_after_date"), null);
    setValue(getStopFieldPath(stopIndex, "stop_after_time"), null);

    const beforeTimePath = getStopFieldPath(stopIndex, "stop_before_time");
    if (!getValues(beforeTimePath)) {
      setValue(beforeTimePath, DEFAULT_DELIVERY_TIME, { shouldDirty: true });
    }
  }, [showServiceLevelSelect, serviceLevel, stopIndex, getValues, setValue]);

  return useMemo(
    () => ({
      isFeatureActive: featureActiveForRequest,
      showServiceLevelSelect,
      showDeliveryDatetimeFields,
      showPickupDateRequiredMessage,
      isDeliveryDatetimeLocked,
      serviceLevel: serviceLevel ?? "",
      computedDeliveryDate: computeDeliveryAfterDateFromServiceLevel(pickupAfterDate, serviceLevel),
      hasPickupDate,
    }),
    [
      featureActiveForRequest,
      showServiceLevelSelect,
      showDeliveryDatetimeFields,
      showPickupDateRequiredMessage,
      isDeliveryDatetimeLocked,
      serviceLevel,
      pickupAfterDate,
      hasPickupDate,
    ],
  );
}

export type DeliveryServiceLevelSlice = ReturnType<typeof useDeliveryServiceLevel>;
